<template>
  <v-app>
    <v-container fluid class="pa-4">
      <v-card v-if="salesContact" class="pa-4" elevation="1" ref="contractCard">
        <!-- Header with logo and title --> 
        <v-row class="mb-4 align-center main-header">
          <v-col cols="12" class="text-center">
            <div class="doc-title">SALES CONTRACT</div>
          </v-col>
        </v-row>

        <!-- Top info blocks -->
        <v-row>
          <v-col cols="6">
            <table class="header-info-table">
              <tbody>
                <tr>
                  <td class="label-cell">Invoice Date</td>
                  <td class="data-cell">{{ formatDate(salesContact.invoice_date) }}</td>
                </tr>
                <tr>
                  <td class="label-cell">Buyer Name</td>
                  <td class="data-cell">{{ salesContact.buyer.company_name }}</td>
                </tr>
                <tr>
                  <td class="label-cell">Address</td>
                  <td class="data-cell">
                    <div>{{ salesContact.buyer.company_address }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-col>

          <v-col cols="6">
            <table class="header-info-table">
              <tbody>
                <tr>
                  <td class="label-cell">Invoice Number</td>
                  <td class="data-cell">{{ salesContact.invoice_no }}</td>
                </tr>
                <tr>
                  <td class="label-cell">SALES CONTRACT NO</td>
                  <td class="data-cell">{{ salesContact.code }}</td>
                </tr>
                <tr>
                  <td class="label-cell">MASTER SALES CONTRACT NO</td>
                  <td class="data-cell">{{ salesContact.master_sales_contact_no }}</td>
                </tr>
                <tr> 
                  <td class="label-cell">Purchase Order (PO) NO</td>
                  <td class="data-cell">{{ salesContact.purchase_order_no || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </v-col>
        </v-row>

        <!-- Supplier / Buyer / Bank blocks -->
        <v-row>
          <v-col cols="6">
            <div class="supplier-info-box">
              <div class="supplier-header">SUPPLIER / BENEFICIARY:</div>
              <div class="supplier-details">
                <div class="company-name">{{ salesContact.branch.company_name }}</div>
                <br />
                <div><span class="text-bold">Address:</span> {{ salesContact.branch.company_address }}</div>
                <div><span class="text-bold">Email:</span> {{ salesContact.branch.company_email }}</div>
                <div><span class="text-bold">Phone:</span> {{ salesContact.branch.company_mobile }}</div>
              </div>
            </div>
          </v-col>

          <v-col cols="6">
            <div class="supplier-info-box">
              <div class="supplier-header">BUYER NAME AND ADDRESS:</div>
              <div class="supplier-details">
                <div class="company-name">{{ salesContact.buyer.company_name }}</div>
                <br />
                <div><span class="text-bold">Address:</span> {{ salesContact.buyer.company_address }}</div>
                <div><span class="text-bold">Email:</span> {{ salesContact.buyer.company_email }}</div>
                <div><span class="text-bold">Mobile:</span> {{ salesContact.buyer.company_mobile }}</div>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Supplier / Buyer / Bank blocks -->
        <v-row>
          <v-col cols="6">
            <div class="supplier-info-box">
              <div class="supplier-header">SUPPLIER / BENEFICIARY BANK DETAILS:</div>
              <div class="supplier-details">
                <div><span class="text-bold">Bank Name:</span> {{ salesContact.beneficiary_bank.name || "N/A" }} </div>
                <div><span class="text-bold">Bank Address:</span> {{ salesContact.beneficiary_bank.address || "N/A" }} </div>
                <div><span class="text-bold">Branch:</span> {{ salesContact.beneficiary_bank.branch || "N/A" }} </div>
                <div><span class="text-bold">Swift Code:</span> {{ salesContact.beneficiary_bank.swift_code || "N/A" }} </div>
                <div><span class="text-bold">A/C No:</span> {{ salesContact.beneficiary_bank.account_no || "N/A" }} </div>
                <div><span class="text-bold">A/C Name:</span> {{ salesContact.beneficiary_bank.account_name || "N/A" }} </div>
                <div><span class="text-bold">Routing No:</span> {{ salesContact.beneficiary_bank.routing_no || "N/A" }} </div>
              </div>
            </div>
          </v-col>

          <v-col cols="6">
            <div class="supplier-info-box">
              <div class="supplier-header">BUYER'S BANK DETAILS:</div>
              <div class="supplier-details">
                <div> <span class="text-bold">Bank Name:</span> {{ salesContact.buyer_bank.name || "N/A" }} </div>
                <div> <span class="text-bold">Bank Address:</span> {{ salesContact.buyer_bank.address || "N/A" }} </div>
                <div> <span class="text-bold">Branch:</span> {{ salesContact.buyer_bank.branch || "N/A" }} </div>
                <div> <span class="text-bold">Swift Code:</span> {{ salesContact.buyer_bank.swift_code || "N/A" }} </div>
                <div> <span class="text-bold">A/C No:</span> {{ salesContact.buyer_bank.account_no || "N/A" }} </div>
                <div> <span class="text-bold">A/C Name:</span> {{ salesContact.buyer_bank.account_name || "N/A" }} </div>
                <div> <span class="text-bold">Routing No:</span> {{ salesContact.buyer_bank.routing_no || "N/A" }} </div>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Details table -->
        <v-row class="mt-4">
          <v-col cols="12">
            <table class="details-table">
              <thead>
                <tr>
                  <th>PO NUMBER</th>
                  <th>STYLE NUMBER</th>
                  <th>DESCRIPTION</th>
                  <th>HS CODE</th>
                  <th>COMPOSITION</th>
                  <th>COLOR NAME</th>
                  <th>SIZE RANGE</th>
                  <th class="text-right">QUANTITY</th>
                  <th class="text-right">UNIT PRICE</th>
                  <th>CURRENCY</th>
                  <th class="text-right">TOTAL AMOUNT</th>
                  <th>CARGO RECEIVE DATE</th>
                  <th>ETD</th>
                  <th>DESTINATION</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in salesContact.details" :key="row.id">
                  <td class="text-center">{{ row.po }}</td>
                  <td class="text-center">{{ row.style_no }}</td>
                  <td class="text-center">{{ row.description }}</td>
                  <td class="text-center">{{ row.hs_code }}</td>
                  <td class="text-center">{{ row.composition }}</td>
                  <td class="text-center">{{ row.color_name }}</td>
                  <td class="text-center">{{ row.size_range }}</td>
                  <td class="text-right">{{ (row.quantity) }}</td>
                  <td class="text-center">{{ formatNumber(row.unit_price) }}</td>
                  <td class="text-center">{{ row.currency?.name }}</td>
                  <td class="text-center">{{ (row.total_amount) }}</td>
                  <td class="text-center">{{ formatDate(row.cargo_receive_date) }}</td>
                  <td class="text-center">{{ formatDate(row.etd_date) }}</td>
                  <td class="text-center">{{ row.destination }}</td>
                </tr>
              </tbody>

              <tbody>
                <tr>
                  <td colspan="10" class="text-right"><strong>GRAND TOTAL</strong></td>
                  <td class="text-right">
                    <strong>{{ formatNumber(salesContact.total_amount) }}</strong>
                  </td>
                  <td colspan="3"></td>
                </tr>

                <tr>
                  <td colspan="4" class="label-cell text-center">Total</td>
                  <td colspan="4" class="label-cell text-center">Amount in Words</td>
                  <td colspan="6" class="data-cell text-center" style="padding-left: 15px;">
                    <strong> {{ convertNumberToWords(salesContact.total_amount) }}
                      ONLY</strong>
                  </td>
                </tr>

                <tr>
                  <td colspan="4" class="label-cell text-center">{{ formattedAdvancePayment }} Advance at Now</td>
                  <td colspan="4" class="label-cell text-center"></td>
                  <td colspan="6" class="data-cell text-center" style="padding-left: 15px;">
                    <strong> {{ salesContact.currency.symbol }} {{ formatNumber(salesContact.advance_payment_amount) }}
                    </strong>
                  </td>
                </tr>

                <tr>
                  <td colspan="4" class="label-cell text-center"> </td>
                  <td colspan="4" class="label-cell text-center">Amount in Words</td>
                  <td colspan="6" class="data-cell text-center">
                    <strong> {{ convertNumberToWords(salesContact.advance_payment_amount) }}
                    </strong>
                  </td>
                </tr>

                <tr>
                  <td colspan="4" class="label-cell text-center">{{ formattedBalancePayment }} Balance will be paid
                    Before Shipment</td>
                  <td colspan="4" class="label-cell text-center"></td>
                  <td colspan="6" class="data-cell text-center" style="padding-left: 15px;">
                    <strong>{{ salesContact.currency?.symbol}} {{ formatNumber(salesContact.before_shipment_payment_amount) }}
                      </strong>
                  </td>
                </tr>

                <tr>
                  <td colspan="4" class="label-cell text-center"></td>
                  <td colspan="4" class="label-cell text-center">Amount in Words</td>
                  <td colspan="6" class="data-cell text-center" style="padding-left: 15px;">
                    <strong>{{ convertNumberToWords(salesContact.before_shipment_payment_amount) }}
                      </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-col>
        </v-row>

        <!-- Payment / Shipment terms -->
        <v-row class="mt-4">
          <v-col cols="12">
            <table class="header-info-table shipment-terms-table">
              <thead>
                <tr>
                  <th colspan="6" class="text-left py-1 px-2">CONDITION OF SHIPMENT:</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="label-cell">FROM</td>
                  <td class="data-cell">{{ salesContact.loading_port.name }}</td>
                  <td class="label-cell">TO</td>
                  <td class="data-cell">{{ salesContact.discharge_port.name }}</td>
                  <td class="label-cell">PAYMENT TERMS</td>
                  <td class="data-cell">{{ salesContact.payment_term.name }}</td>
                </tr>
                <tr>
                  <td class="label-cell">SHIPMENT MODE</td>
                  <td class="data-cell">{{ salesContact.shipping_method.name }}</td>
                  <td class="label-cell">INCOTERM</td>
                  <td class="data-cell">{{ salesContact.incoterm.name }}</td>
                  <td class="label-cell">DATE OF EXPIRY</td>
                  <td class="data-cell expiry-highlight">{{ formatDate(salesContact.expiry_date) }}</td>
                </tr>
                <tr>
                  <td class="label-cell">INSPECTION CERTIFICATE BY</td>
                  <td class="data-cell">ANOOSHA</td>
                  <td class="label-cell">SHORT/EXCESS SHIPMENT</td>
                  <td class="data-cell">SHORT SHIPMENT NOT ALLOWED</td>
                  <td class="label-cell">PARTIAL SHIPMENT</td>
                  <td class="data-cell">{{ salesContact.partial_shipment }}</td>
                </tr>
                <tr>
                  <td class="label-cell">PORT OF LOADING</td>
                  <td class="data-cell">{{ salesContact.loading_port.name }}</td>
                  <td class="label-cell">PORT OF DESTINATION</td>
                  <td class="data-cell">{{ salesContact.discharge_port.name }}</td>
                  <td class="label-cell">EARLY SHIPMENT</td>
                  <td class="data-cell">{{ salesContact.early_shipment }}</td>
                </tr>
                <tr>
                  <td colspan="4"></td>
                  <td class="label-cell">TRANSHIPMENT</td>
                  <td class="data-cell">{{ salesContact.transhipment_shipment }}</td>
                </tr>
              </tbody>
            </table>
          </v-col>
        </v-row>

        <!-- Additional condition -->
        <v-row class="mt-4">
          <v-col cols="12">
            <table class="header-info-table shipment-terms-table">
              <thead>
                <tr>
                  <th class="text-left py-1 px-2">ADDITIONAL CONDITION:</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="data-cell text-left additional-condition-cell">
                    <div style="white-space: pre-line">
                      {{ salesContact.additional_condition }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-col>
        </v-row>

        <!-- Signatures -->
        <v-row class="mt-8">
          <v-col cols="6" class="text-center">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">SEAL & SIGNATURE OF THE SUPPLIER / BENEFICIARY</div>
            </div>
          </v-col>
          <v-col cols="6" class="text-center">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">SEAL & SIGNATURE OF THE BUYER</div>
            </div>
          </v-col>
        </v-row>

        <!-- Actions -->
        <v-row class="mt-4">
          <v-col cols="12" class="text-right">
            <v-btn color="primary" variant="tonal" class="mr-2" @click="goBack">
              Back
            </v-btn>
            <v-btn color="primary" @click="printPage">
              Print
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-container>
  </v-app>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import convertNumberToWords from "@/plugins/numberToWord";

const route = useRoute();
const router = useRouter();
const store = useStore();
const contractCard = ref(null);

const id = route.params.id;
const salesContact = ref(null);

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString();
};

const formattedAdvancePayment = computed(() => {
  if (!salesContact.value) return '';
  const value = salesContact.value.advance_payment_value || 0;
  const type = salesContact.value.advance_payment_type;

  if (type === 'percentage' || type === 'percent') {
    return `${value}%`;
  } else if (type === 'flat') {
    return `${value}`;
  } else {
    return `${value} ${type}`;
  }
});

const formattedBalancePayment = computed(() => {
  if (!salesContact.value) return '';
  const value = salesContact.value.before_shipment_payment_value || 0;
  const type = salesContact.value.before_shipment_payment_type;

  if (type === 'percentage' || type === 'percent') {
    return `${value}%`;
  } else if (type === 'flat') {
    return `${value}`;
  } else {
    return `${value} ${type}`;
  }
});

const formatNumber = (val) => {
  if (val == null || val === "") return "";
  return Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const goBack = () => {
  router.push({ name: "sales-contact-record" });
};

const printPage = () => {
  if (!salesContact.value) {
    alert('Please wait for sales contract data to load');
    return;
  }

  if (!contractCard.value) {
    alert('Unable to find contract document');
    return;
  }

  // Clone the entire contract card
  const cardElement = contractCard.value.$el;
  const clonedCard = cardElement.cloneNode(true);

  // Remove the action buttons row from the clone
  const actionButtons = clonedCard.querySelector('div:has(> .v-btn)');
  if (actionButtons) {
    actionButtons.remove();
  }

  // Create print window with enhanced styles
  const printWindow = window.open("", "_blank");
  const printStyles = `
    <style>
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
      }

      body {
        font-family: 'Times New Roman', serif;
        padding: 15px;
        background: white;
        line-height: 1.4;
        font-size: 12px;
      }

      /* Hide Vuetify layout components but keep content */
      .v-app__wrap,
      .v-app {
        all: unset;
        display: block !important;
      }

      .v-container {
        padding: 0 !important;
        margin: 0 !important;
        width: 100% !important;
        max-width: none !important;
      }

      .v-row {
        display: flex !important;
        flex-wrap: wrap !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
      }

      .v-col {
        padding: 4px !important;
        margin: 0 !important;
      }

      .v-col-6 {
        flex: 0 0 calc(50% - 8px) !important;
        width: calc(50% - 8px) !important;
      }

      .v-col-12 {
        flex: 0 0 100% !important;
        width: 100% !important;
      }

      .v-col-3 {
        flex: 0 0 calc(25% - 8px) !important;
        width: calc(25% - 8px) !important;
      }

      .v-card {
        box-shadow: none !important;
        border: none !important;
        background: white !important;
        padding: 0 !important;
      }

      /* Text alignment utilities */
      .text-left {
        text-align: left !important;
      }

      .text-center {
        text-align: center !important;
      }

      .text-right {
        text-align: right !important;
      }

      .align-center {
        align-items: center !important;
      }

      .mb-4 {
        margin-bottom: 20px !important;
      }

      .mt-4 {
        margin-top: 20px !important;
      }

      .mt-8 {
        margin-top: 40px !important;
      }

      .pa-4 {
        padding: 0 !important;
      }

      .mr-2 {
        margin-right: 0 !important;
      }

      .py-1 {
        padding-top: 4px !important;
        padding-bottom: 4px !important;
      }

      .px-2 {
        padding-left: 8px !important;
        padding-right: 8px !important;
      }

      /* Table styles */
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 10px 0;
        border: 1px solid #000;
      }

      th, td {
        border: 1px solid #000;
        padding: 6px 8px;
        text-align: left;
        font-size: 11px;
      }

      th {
        background-color: #eceff1;
        font-weight: 600;
      }

      .header-info-table {
        font-size: 12px;
        border: 1px solid #757575;
        margin: 10px 0;
      }

      .header-info-table td {
        border: 1px solid #757575;
        padding: 4px 8px;
        height: 22px;
        vertical-align: middle;
        font-size: 11px;
      }

      .label-cell {
        color: black;
        font-weight: bold;
        background-color: #f5f5f5;
        width: 40%;
      }

      .data-cell {
        background-color: #ffffff;
        font-weight: 600;
      }

      .main-header {
        border: 1px solid black;
        margin: 0px;
      }

      .company-name {
        font-weight: 700;
        font-size: 14px;
        text-transform: uppercase;
        margin-bottom: 4px;
      }

      .doc-title {
        font-weight: 700;
        font-size: 18px;
      }

      .logo-placeholder {
        width: 90px;
        height: 60px;
        border: 1px solid #b0bec5;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
      }

      .supplier-info-box {
        border: 1px solid #000;
        padding: 8px;
        min-height: 120px;
        font-family: Arial, sans-serif;
        color: #000;
        margin: 8px 0;
      }

      .supplier-header {
        font-weight: bold;
        text-decoration: underline;
        text-transform: uppercase;
        font-size: 11px;
        margin-bottom: 6px;
      }

      .supplier-details {
        font-size: 10px;
        line-height: 1.5;
      }

      .supplier-details div {
        margin: 2px 0;
      }

      .details-table {
        font-size: 9px;
        margin: 15px 0;
      }

      .details-table th,
      .details-table td {
        padding: 4px;
        text-align: center;
        border: 1px solid #000;
      }

      .details-table th {
        background-color: #eceff1;
        font-weight: 600;
      }

      .shipment-terms-table {
        width: 100%;
        border: 1px solid #000;
        border-collapse: collapse;
        font-size: 10px;
        margin: 15px 0;
      }

      .shipment-terms-table th {
        background-color: #ffffff;
        border: 1px solid #000;
        text-transform: uppercase;
        font-weight: bold;
        padding: 6px;
        text-align: left;
      }

      .shipment-terms-table td {
        border: 1px solid #000;
        padding: 6px;
        text-transform: uppercase;
        font-weight: 600;
        font-size: 10px;
      }

      .shipment-terms-table .label-cell {
        background-color: transparent;
        width: 20%;
        font-weight: bold;
      }

      .shipment-terms-table .data-cell {
        width: 30%;
        text-align: left;
      }

      .additional-condition-cell {
        min-height: 100px;
        vertical-align: top !important;
        padding: 10px !important;
        line-height: 1.5;
        text-align: left !important;
        background-color: #ffffff;
        width: 100%;
        font-size: 11px;
        white-space: pre-wrap;
        word-wrap: break-word;
        font-family: Arial, sans-serif;
      }

      .expiry-highlight {
        background-color: #ffff00 !important;
      }

      .signature-box {
        border: 1px solid black;
        border-radius: 5px;
        padding: 10px;
        font-size: 11px;
        text-align: center;
        margin-top: 20px;
      }

      .signature-line {
        border-bottom: 1px solid #000;
        margin: 50px 0 8px 0;
        height: 30px;
      }

      .signature-label {
        text-transform: uppercase;
        font-weight: bold;
        font-size: 10px;
      }

      /* Hide print buttons */
      .v-btn {
        display: none !important;
      }

      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }

        html, body {
          margin: 0;
          padding: 10px;
        }

        body {
          padding: 10px;
        }

        table {
          page-break-inside: avoid;
          border: 1px solid #000;
        }

        tr {
          page-break-inside: avoid;
        }

        .signature-box {
          page-break-inside: avoid;
        }

        .supplier-info-box {
          page-break-inside: avoid;
        }

        .v-btn {
          display: none !important;
        }
      }

      @page {
        size: portrait;
        margin: 0.5cm;
      }
    </style>
  `;

  printWindow.document.write(
    `<!DOCTYPE html>
      <html>
        <head>
          ${printStyles}
        </head>
      <body>
        ${clonedCard.outerHTML}
      </body>
    </html>
    `
  );

  printWindow.document.close();
  printWindow.focus();

  setTimeout(() => {
    printWindow.print();
  }, 500);
};

onMounted(async () => {
  const res = await store.dispatch("getSalesContact", id);
    if (res?.data?.success) {
      salesContact.value = res.data.data;
    }
});
</script>

<style scoped>
.main-header {
  border: 1px solid black;
  margin: 0px;
}

.company-name {
  font-weight: 700;
  font-size: 16px;
  text-transform: uppercase;
}

.text-bold{
  font-weight: bold;
}

.doc-title {
  font-weight: 700;
  font-size: 18px;
  margin-top: 4px;
}

.logo-placeholder {
  width: 90px;
  height: 60px;
  border: 1px solid #b0bec5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.supplier-info-box {
  border: 1px solid #000;
  padding: 8px;
  min-height: 120px;
  font-family: Arial, sans-serif;
  color: #000;
}



.supplier-header {
  font-weight: bold;
  text-decoration: underline;
  text-transform: uppercase;
  font-size: 12px;
  margin-bottom: 4px;
}

.supplier-details {
  font-size: 11px;
  line-height: 1.4;
}

.header-info-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  border: 1px solid #757575;
}

.header-info-table td {
  border: 1px solid #757575;
  padding: 4px 8px;
  height: 25px;
  vertical-align: middle;
}

.label-cell {
  color: black;
  font-weight: bold;
  width: 50%;
  text-align: left;
}

.data-cell {
  background-color: #ffffff;
  font-weight: 600;
  text-align: left;
}

.details-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.details-table th,
.details-table td {
  border: 1px solid black;
  padding: 4px;
}

.details-table th {
  background-color: #eceff1;
  font-weight: 600;
  text-align: center;
}

.signature-box {
  font-size: 11px;
  border: 1px solid black;
  border-radius: 5px;
  padding: 10px;
}

.signature-line {
  border-bottom: 1px solid #000;
  margin-bottom: 8px;
  margin-top: 40px;
}

.signature-label {
  text-transform: uppercase;
  font-weight: bold;
  font-size: 10px;
}

.shipment-terms-table {
  width: 100%;
  border: 1px solid #000;
  border-collapse: collapse;

}

.shipment-terms-table thead th {
  background-color: #ffffff;
  border: 1px solid #000;
  text-transform: uppercase;
  font-weight: bold;
}

.shipment-terms-table td {
  border: 1px solid #000;
  padding: 2px 6px;
  height: 22px;
  text-transform: uppercase;
  font-weight: 700;
}

/* Specific highlight for Expiry Date as seen in your image */
/* .expiry-highlight {
  background-color: #ffff00 !important;
} */

/* Adjust label width for the shipment table grid */
.shipment-terms-table .label-cell {
  width: 15%;
  background-color: transparent; /* Labels in this section aren't orange in your image */
  font-weight: bold;
}

.shipment-terms-table .data-cell {
  width: 18%;
  text-align: center;
}


.additional-condition-cell {
  min-height: 100px; /* Gives space for long text blocks */
  vertical-align: top !important; /* Ensures text starts at the top */
  padding: 10px !important;
  line-height: 1.5;
  text-align: left !important;
  background-color: #ffffff;
  width: 100%;
}

/* Ensure the header in this section matches the Shipment table header */
.shipment-terms-table thead th {
  border: 1px solid #000;
  background-color: #ffffff;
  font-weight: bold;
  font-size: 11px;
}
</style>
